import { getStorageItem } from "@/helpers/storage.helpers";
import type { Environment, ExcludedEnvs } from "@/types/common";

/**
 * Various helpers for "feature switching" logic.
 *
 * Initially provides conditionals for environments only.
 *
 * @module switch.helpers
 */

export type EnvName = "prod" | "dev" | "sim" | "local";

const simRE = /^se-\d+\.simulation\.arion\.arenko\.group$/;

export const currentEnv = () =>
  String(import.meta.env.VITE_DEPLOY_ENV ?? "local") as Environment;

/**
 * Assert the runtime environment.
 * This matches on the hostname displayed in the browser url.
 *
 * You can override the setting by adding a localStorage value, e.g.
 * ```
 * localStorage.setItem("isEnvOverride", "true");
 * ```
 * @deprecated use `import.meta.env.VITE_DEPLOY_ENV` instead
 */
export const isEnv = (envName: EnvName): boolean => {
  const { hostname } = globalThis.location;
  const { localStorage } = globalThis;

  if (localStorage) {
    const override = localStorage.getItem("isEnvOverride");
    if (override === "true") {
      return true;
    }
    if (override === "false") {
      return false;
    }
  }

  switch (envName) {
    case "local": {
      return hostname === "localhost";
    }
    case "dev": {
      return hostname === "dev.simulation.arion.arenko.group";
    }
    case "sim": {
      return simRE.test(hostname);
    }
    case "prod": {
      return hostname === "nimbus.arenko.group";
    }
    default:
      return false;
  }
};

export const externalEnvs: (Environment | RegExp)[] = [
  "prod",
  /clientsandbox*/,
];

const envInList = (list: (Environment | RegExp)[], env: string) =>
  list.some((exclude) =>
    exclude instanceof RegExp ? exclude.test(env) : exclude.includes(env)
  );

type SwitchedEnvs = (Environment | RegExp)[];

/**
 * Test for feature switch in given environments.
 *
 * @param storageKey  name of the feature switch
 * @param switchedEnvs  [optional] environments where switch is used (default to external envs)
 * @param env  [optional] defaults to current environment
 */
export const isFeatureSwitchEnabled = (
  storageKey: string,
  switchedEnvs: SwitchedEnvs = externalEnvs,
  env: Environment = currentEnv()
) => {
  if (envInList(switchedEnvs, env)) {
    return getStorageItem(storageKey, false);
  }
  return true;
};

/**
 * Partial application to test for feature switch disabled state.
 * For use in the routingConfig
 *
 * @param storageKey  name of the feature switch
 * @param switchedEnvs  [optional] environments where switch is used (default to external envs)
 * @returns {(env: Environment) => boolean}
 */
export const isFeatureSwitchDisabled =
  (storageKey: string, switchedEnvs: SwitchedEnvs = externalEnvs) =>
  (env: Environment) =>
    !isFeatureSwitchEnabled(storageKey, switchedEnvs, env);

/**
 * Checks if current env is in excluded list
 */
export const isExcludedEnv = (excludedEnvs: ExcludedEnvs = []) => {
  const env = currentEnv();
  if (typeof excludedEnvs === "function") {
    return excludedEnvs(env);
  }

  return envInList(excludedEnvs, env);
};
